import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Grid, useTheme } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import { InfoLanguage } from '@bandido/shared/enums';
import { NimbuxHotelAmenity } from '@bandido/shared/interfaces';
import { DynamicMUIIcon } from '@frontend/features/common/utils/iconResolver';

interface HotelAmenitiesProps {
  amenities: NimbuxHotelAmenity[];
}

export const HotelAmenities = ({ amenities }: HotelAmenitiesProps) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  if (!amenities || amenities.length === 0) return null;

  const uniqueAmenities = Array.from(
    new Set(amenities?.map((amenity) => amenity.texts[i18n.language as InfoLanguage])),
  ).map((amenity) => amenities.find((a) => a.texts[i18n.language as InfoLanguage] === amenity));

  const popularAmenities = uniqueAmenities.slice(0, 6);
  const hasMoreAmenities = uniqueAmenities.length > 6;

  const handleToggleAmenities = () => {
    setShowAllAmenities(!showAllAmenities);
  };

  return (
    <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">{t('hotelContent.nimbuxHotels.card.popularAmenities')}</Typography>

      <Grid container sx={{ p: 0 }}>
        {(showAllAmenities ? uniqueAmenities : popularAmenities).map((amenity) => (
          <Grid gap={1} sx={{ p: 0 }} item xs={12} md={6} lg={4} key={amenity?.id}>
            <Box display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
              <DynamicMUIIcon color="disabled" iconName={amenity?.icon || ''} />
              <Typography variant="body2">{amenity?.texts[i18n.language as InfoLanguage]}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {hasMoreAmenities && (
        <Box sx={{ textAlign: 'left' }}>
          <Button
            variant="text"
            onClick={handleToggleAmenities}
            endIcon={showAllAmenities ? <ExpandLess /> : <ExpandMore />}
            sx={{
              ...theme.typography.subtitle2,
              textTransform: 'none',
            }}
          >
            {showAllAmenities
              ? t('hotelContent.nimbuxHotels.card.showLessAmenities')
              : t('hotelContent.nimbuxHotels.card.showAllAmenities')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
